import type { SyntheticEvent } from "react"
import type { AdvancedProductCardMolProps } from "@onestore/hel/dist/components/molecules/AdvancedProductCardMol"
import { PriceVariantType } from "@onestore/hel/dist/components/molecules/PriceMol/priceMol.types"
import _t from "@core/i18n"
import { PeriodInfo } from "@core/period-info"
import { PriceFormat } from "@core/types"
import DetailedPrice from "@gatsby-plugin-generic-page/components/DetailedPrice"
import getProductCardPeriodText from "@gatsby-plugin-generic-page/helpers/getProductCardPeriodText"
import { getUpsellData } from "@gatsby-plugin-generic-page/helpers/getUpsellData"
import type { ProductCardItem } from "@gatsby-plugin-marketplace/fragments/marketplaceProductCard"
import { get } from "lodash"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import usePriceConfigurationQuery from "~/hooks/usePriceConfigurationQuery"
import assetsSquidexToImageObject from "~/lib/assetsSquidexToImageObject"
import isEmpty from "~/lib/isEmpty"
import url from "~/lib/url"
import { showPlanOverLimitModal } from "~/store/basket/actions"
import { getPlansUsageOverLimit } from "~/store/basket/selectors"
import { useAppDispatch, useAppSelector } from "~/store/hooks"
import Price from "../components/AdvancedProductCard/components/Price"
import type { LinkPropertiesCollectionHook } from "./useLinkPropertiesCollection"

export default function useAdvancedProductCard(
  productData: ProductCardItem,
  linkData: LinkPropertiesCollectionHook
): AdvancedProductCardMolProps {
  const dispatch = useAppDispatch()
  const plansOverLimit = useAppSelector(getPlansUsageOverLimit)
  const { currentPriceType, isNettoPriceType } = usePriceTypeContext()
  const { showDetailedPrices } = usePriceConfigurationQuery()

  const {
    name,
    headerCardBg,
    headerLogo,
    cardDescription,
    priceText,
    pages,
    saleConfigurations,
    hasPricePrefix,
  } = productData.flatData

  const backgroundImage = assetsSquidexToImageObject(headerCardBg, name)

  const logo = {
    image: assetsSquidexToImageObject(headerLogo, name),
  }

  const description = cardDescription || ""

  const pageUrl = pages[0].flatData.url

  const link = {
    ...linkData[pageUrl],
    text: _t("viewDetails"),
    title: name,
  }

  const saleConfiguration =
    !isEmpty(saleConfigurations) && saleConfigurations[0]

  const parameters = saleConfiguration
    ? saleConfiguration.flatData.config.parameters
    : undefined

  const defaultCloudBluePlan = saleConfiguration
    ? saleConfiguration.flatData.defaultCloudBluePlan
    : undefined

  const bonusLink =
    saleConfiguration &&
    url.generateBonusUrl({
      p: !isEmpty(defaultCloudBluePlan)
        ? defaultCloudBluePlan[0].flatData.alias
        : undefined,
      ax: getUpsellData(saleConfiguration.flatData.upsell),
      priceType: currentPriceType,
    })

  const optionsMapping = saleConfiguration
    ? saleConfiguration.flatData.optionsMapping
    : undefined

  const defaultCloudBluePlanPeriods = !isEmpty(defaultCloudBluePlan)
    ? defaultCloudBluePlan[0].flatData.periods
    : undefined

  const defaultPeriod = !isEmpty(defaultCloudBluePlanPeriods)
    ? defaultCloudBluePlanPeriods.find((period) => period.default)
    : undefined

  const periodInfo = defaultPeriod ? new PeriodInfo(defaultPeriod) : undefined

  const renewPrice =
    periodInfo && periodInfo.period.price_renew
      ? periodInfo.getFormatted(
          isNettoPriceType
            ? PriceFormat.RENEW_NETTO_NO_CURRENCY_NO_PERIOD
            : PriceFormat.RENEW_GROSS_NO_CURRENCY_NO_PERIOD
        )
      : undefined

  const suffix = periodInfo
    ? periodInfo.getFormatted(
        isNettoPriceType
          ? PriceFormat.NETTO_WITH_PERIOD
          : PriceFormat.GROSS_WITH_PERIOD
      )
    : undefined

  const prefix =
    !isEmpty(optionsMapping) || hasPricePrefix ? _t("prices.from") : undefined

  const currency = periodInfo
    ? periodInfo.getFormatted(PriceFormat.CURRENCY)
    : undefined

  const periodText = getProductCardPeriodText(periodInfo, currentPriceType)

  const tooltip = !isEmpty(defaultCloudBluePlan)
    ? get(
        defaultCloudBluePlan[0],
        "flatData.promotionPriceTooltip[0].flatData.text"
      )
    : undefined

  const isPlanOverLimit = !isEmpty(defaultCloudBluePlan)
    ? plansOverLimit.includes(defaultCloudBluePlan[0].flatData.id)
    : false

  const onClickButton = (event: SyntheticEvent) => {
    if (isPlanOverLimit) {
      return dispatch(showPlanOverLimitModal())
    }

    if (!bonusLink) {
      return link.onClick && link.onClick(event)
    }

    return undefined
  }

  const button = {
    text: _t(bonusLink ? "productCard.orderNow" : "productCard.check"),
    href: isPlanOverLimit ? undefined : bonusLink || link.href,
    onClick: onClickButton,
    title: name,
  }

  const price = periodInfo ? (
    <Price
      prefix={prefix}
      periodInfo={periodInfo}
      priceType={currentPriceType}
      periodText={periodText}
      tooltip={tooltip}
    />
  ) : undefined

  const priceInfo = !isEmpty(defaultCloudBluePlan)
    ? defaultCloudBluePlan[0].flatData.priceInfo
    : undefined

  const detailedPriceJSX =
    showDetailedPrices && !isEmpty(periodInfo) ? (
      <div>
        <DetailedPrice
          variant={{ type: PriceVariantType.SMALL, align: "left" }}
          periodInfo={periodInfo}
          priceInfo={priceInfo}
        />
      </div>
    ) : null

  const renewPriceObject = renewPrice
    ? {
        label: _t("productCard.renewPrice"),
        value: renewPrice,
        suffix: `${currency} ${suffix}`,
      }
    : undefined

  const hasPromotion = periodInfo?.hasPromotion() ?? false

  return {
    label: hasPromotion
      ? {
          children: _t("marketplace.discountTitle"),
          variant: "primaryOutlined",
        }
      : undefined,
    backgroundImage,
    title: name,
    logo,
    link,
    description,
    price: detailedPriceJSX || price,
    renewPrice: renewPriceObject,
    priceText,
    parameters,
    button,
  }
}
